import {
    Extension,
    pointerUtils,
    CreateExtensionArgument,
    type CreateExtArgs,
    type ExtensionAPI,
    type DalValue,
    type ExtensionPublicAPI
} from '@wix/document-manager-core'
import type {FragmentPointer, Pointer} from '@wix/document-services-types'
import {RESOURCE_TYPES} from '../../constants/constants'
import type {FragmentsExtensionAPI} from '../fragments/fragments'

const {getFragmentPointer} = pointerUtils

interface EditorFeaturesAPI {
    editorFeatures: {
        instances: {
            list(): string[]
            create(name?: string): void
        }
        data: {
            getPointer(fragmentInstanceId: string, namespace: string, itemId: string): Pointer | FragmentPointer
            list(namespace: string): (Pointer | FragmentPointer)[]
            get(pointer: Pointer | FragmentPointer): DalValue
            remove(pointer: Pointer | FragmentPointer): void
            update(pointer: Pointer | FragmentPointer, value: DalValue): void
        }
    }
}

export interface EditorFeaturesPublicAPI extends EditorFeaturesAPI, ExtensionPublicAPI {}
export interface EditorFeatureExtensionAPI extends EditorFeaturesAPI, ExtensionAPI {}

const isValidEditorSettingsPointer = (pointer: Pointer) =>
    pointer.fragmentType === RESOURCE_TYPES.EDITOR_FEATURES && pointer.fragmentInstance && pointer.type && pointer.id

const validateEditorSettingsPointer = (pointer: Pointer) => {
    if (!isValidEditorSettingsPointer(pointer)) {
        throw new Error(
            `The given pointer ${JSON.stringify(pointer)} is not a valid ${RESOURCE_TYPES.EDITOR_FEATURES} pointer`
        )
    }
}
const createExtension = ({experimentInstance}: CreateExtensionArgument): Extension => {
    const createExtensionAPI = (createExtArgs: CreateExtArgs): EditorFeatureExtensionAPI => {
        const {dal, extensionAPI} = createExtArgs
        const {fragments, fragmentIndex} = extensionAPI as FragmentsExtensionAPI

        const throwIfExperimentClosed = () => {
            if (!experimentInstance.isOpen('dm_fragmentsEditorFeatures')) {
                throw new Error(`The experiemnt dm_fragmentsEditorFeatures must be openned to use this api`)
            }
        }

        return {
            editorFeatures: {
                instances: {
                    list() {
                        throwIfExperimentClosed()
                        return fragmentIndex.list(RESOURCE_TYPES.EDITOR_FEATURES).map(({id}) => id)
                    },
                    create(name?: string) {
                        throwIfExperimentClosed()
                        if (!name) {
                            throw new Error('Instance name is required')
                        }
                        // Use fragments API to create fragment index entry
                        fragments.addFragmentIndex({
                            type: RESOURCE_TYPES.EDITOR_FEATURES,
                            name
                        })
                    }
                },
                data: {
                    getPointer(fragmentInstance: string, type: string, id: string) {
                        throwIfExperimentClosed()
                        return getFragmentPointer({
                            fragmentType: RESOURCE_TYPES.EDITOR_FEATURES,
                            fragmentInstance,
                            type,
                            id
                        })
                    },
                    list(namespace: string) {
                        throwIfExperimentClosed()
                        return dal.getItemsInNamespace(RESOURCE_TYPES.EDITOR_FEATURES, namespace)
                    },
                    get(pointer: FragmentPointer) {
                        throwIfExperimentClosed()
                        validateEditorSettingsPointer(pointer)
                        return dal.get(pointer)
                    },
                    remove(pointer: FragmentPointer) {
                        throwIfExperimentClosed()
                        validateEditorSettingsPointer(pointer)
                        dal.remove(pointer)
                    },
                    update(pointer: FragmentPointer, value: DalValue) {
                        throwIfExperimentClosed()
                        validateEditorSettingsPointer(pointer)
                        value.metaData = {fragmentInstance: pointer.fragmentInstance}
                        dal.set(pointer, value)
                    }
                }
            }
        }
    }

    const createPublicAPI = (createExtArgs: CreateExtArgs): EditorFeaturesPublicAPI => {
        const {editorFeatures} = createExtArgs.extensionAPI as EditorFeatureExtensionAPI
        return {
            editorFeatures: {
                instances: {
                    list: () => editorFeatures.instances.list(),
                    create: (name?: string) => editorFeatures.instances.create(name)
                },
                data: {
                    getPointer: (fragmentInstance: string, type: string, id: string) =>
                        editorFeatures.data.getPointer(fragmentInstance, type, id),
                    list: (namespace: string) => editorFeatures.data.list(namespace),
                    get: (pointer: FragmentPointer) => editorFeatures.data.get(pointer),
                    remove: (pointer: FragmentPointer) => editorFeatures.data.remove(pointer),
                    update: (pointer: FragmentPointer, value: DalValue) => editorFeatures.data.update(pointer, value)
                }
            }
        }
    }

    return {
        name: 'editorFeatures',
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
